import { useState } from 'react'
import { Botao } from '../../components/Botao'
import { AvisoDeErro } from '../../components/Estados'
import { CampoDeTexto } from '../../components/campos'
import { TEXTOS } from '../../lib/glossario'
import { useRegistrarRecebimento, useRevogarRecebimento } from '../../api/queries/relatorios'
import type { ChecklistDeRecebimento as Checklist, LinhaDoChecklist } from '../../types/relatorios'
import { DialogoDeTexto } from '../chamados/fiscalizacao/DialogoDeTexto'
import { dataPorExtenso } from '../iad/pontuacao'

/**
 * Os relatórios que a contratada deve entregar no mês, e quais chegaram.
 *
 * **A ausência é a informação.** Falta de evidência tem consequência contratual expressa, e uma
 * consequência que depende de alguém lembrar não é consequência.
 *
 * **A tela não diz "atrasado" sem prazo fixado.** O contrato exige os relatórios "mensalmente" e
 * nunca escreve o dia — apontar atraso sem prazo seria cobrar obrigação que o contrato não criou.
 *
 * @param props o checklist do mês
 * @returns a lista dos quatro relatórios cobrados
 */
export function ChecklistDeRecebimento({ checklist }: { checklist: Checklist }) {
  const t = TEXTOS.relatorios.recebimento
  const [retirando, setRetirando] = useState<string | null>(null)
  const revogar = useRevogarRecebimento()

  return (
    <div className="space-y-4">
      {checklist.prazoNaoFixado && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-inset ring-slate-200">
          {t.semPrazoFixado}
        </p>
      )}

      <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
        {checklist.linhas.map((linha) => (
          <Linha key={linha.tipo.codigo} linha={linha} aoRetirar={() => setRetirando(linha.tipo.codigo)} />
        ))}
      </ul>

      <Formulario checklist={checklist} />

      {retirando && (
        <DialogoDeTexto
          titulo={t.retirar}
          ajuda={t.retirarAjuda}
          rotulo="Por que está sendo retirado"
          aoConfirmar={(motivo) => {
            revogar.mutate({ competencia: checklist.competencia, tipo: retirando, motivo })
            setRetirando(null)
          }}
          aoFechar={() => setRetirando(null)}
        />
      )}
    </div>
  )
}

/** Um relatório cobrado: chegou ou não chegou, e o que o contrato pede nele. */
function Linha({ linha, aoRetirar }: { linha: LinhaDoChecklist; aoRetirar: () => void }) {
  const t = TEXTOS.relatorios.recebimento
  const entregue = linha.entregueEm != null

  return (
    <li className="space-y-1.5 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <span className="font-medium text-slate-900">{linha.tipo.rotulo}</span>
          <p className="mt-0.5 max-w-prose text-xs text-slate-600">{linha.tipo.descricao}</p>
          <p className="text-xs text-slate-500">{linha.tipo.fonteDoTr}</p>
        </div>
        <Situacao linha={linha} />
      </div>

      {entregue && (
        <div className="text-xs text-slate-600">
          {t.entregueEm(dataPorExtenso(linha.entregueEm))} · {linha.referencia}
          {linha.observacao && <span className="block text-slate-500">{linha.observacao}</span>}
        </div>
      )}

      {entregue && <Botao onClick={aoRetirar}>{t.retirar}</Botao>}
    </li>
  )
}

/** Entregue, atrasado ou não entregue — e "não entregue" nunca vira "no prazo". */
function Situacao({ linha }: { linha: LinhaDoChecklist }) {
  const t = TEXTOS.relatorios.recebimento
  if (linha.entregueEm == null) {
    return (
      <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900">
        {t.naoEntregue}
      </span>
    )
  }
  if (linha.diasDeAtraso != null) {
    return (
      <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-900">
        {t.atrasado(linha.diasDeAtraso)}
      </span>
    )
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-900">
      {t.entregue}
    </span>
  )
}

/** O registro de um recebimento novo. */
function Formulario({ checklist }: { checklist: Checklist }) {
  const t = TEXTOS.relatorios.recebimento
  const registrar = useRegistrarRecebimento()
  const pendentes = checklist.linhas.filter((l) => l.entregueEm == null)
  const [tipo, setTipo] = useState('')
  const [entregueEm, setEntregueEm] = useState('')
  const [referencia, setReferencia] = useState('')

  if (pendentes.length === 0) {
    return <p className="text-sm text-emerald-800">{t.tudoEntregue}</p>
  }

  const completo = tipo !== '' && entregueEm !== '' && referencia.trim() !== ''

  return (
    <section className="cartao space-y-3 p-4" aria-label={t.registrar}>
      <h3 className="text-sm font-semibold text-slate-900">{t.registrar}</h3>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-sm">
          <span className="block font-medium text-slate-800">Qual relatório</span>
          <select
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Escolha…</option>
            {pendentes.map((l) => (
              <option key={l.tipo.codigo} value={l.tipo.codigo}>
                {l.tipo.rotulo}
              </option>
            ))}
          </select>
        </label>
        <CampoDeTexto
          rotulo="Quando chegou"
          type="date"
          value={entregueEm}
          onChange={(e) => setEntregueEm(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Onde ele está"
          ajuda={t.referenciaAjuda}
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
        />
      </div>

      {registrar.isError && <AvisoDeErro erro={registrar.error} />}

      <Botao
        aparencia="primario"
        disabled={!completo || registrar.isPending}
        onClick={() =>
          registrar.mutate(
            { competencia: checklist.competencia, tipo, entregueEm, referencia: referencia.trim() },
            {
              onSuccess: () => {
                setTipo('')
                setEntregueEm('')
                setReferencia('')
              },
            },
          )
        }
      >
        {t.registrar}
      </Botao>
    </section>
  )
}
